import { cn } from '@/lib/utils';

interface FacilityTabsProps {
  facilities: string[];
  /** null = "Alla" */
  selected: string | null;
  onSelect: (facility: string | null) => void;
}

/**
 * FacilityTabs — horizontal chip menu over facilities.
 *
 * "Alla" first, then one chip per facility. The active chip inverts to
 * solid primary (white-on-charcoal in dark mode); inactive chips are
 * frosted glass. Hidden entirely when there is only one facility, since
 * filtering would be a no-op.
 */
export function FacilityTabs({ facilities, selected, onSelect }: FacilityTabsProps) {
  if (facilities.length <= 1) return null;

  const chips: Array<{ label: string; value: string | null }> = [
    { label: 'Alla', value: null },
    ...facilities.map((f) => ({ label: f, value: f as string | null })),
  ];

  return (
    <div
      role="tablist"
      aria-label="Fastigheter"
      className="flex gap-2 overflow-x-auto scrollbar-none -mx-4 px-4 py-1"
    >
      {chips.map(({ label, value }) => {
        const isActive = selected === value;
        return (
          <button
            key={label}
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(value)}
            className={cn(
              'shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-all active:scale-95',
              isActive
                ? 'bg-primary text-primary-foreground shadow-[0_6px_20px_-6px_hsl(var(--glass-shadow))]'
                : 'glass-chrome text-foreground/80 hover:text-foreground'
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
